import { computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../auth.service';
import { ConfigService, type SystemConfig } from '../config.service';
import {
  RepoSelectsService,
  splitRepoCodeUrl,
  type RepoSelectOption,
} from './repo-selects.service';

export interface HeaderRepoSelectOption {
  label: string;
  route: string;
}

function matchCurrentRepoLabel(
  items: RepoSelectOption[],
  systems: SystemConfig[],
  fallback: string,
): string {
  if (typeof window === 'undefined') {
    return fallback;
  }

  const origin = window.location.origin;
  const apiMatch = items.find((item) => {
    const split = splitRepoCodeUrl(item.code);
    return split?.baseUrl === origin;
  });
  if (apiMatch?.text) {
    return apiMatch.text;
  }

  const configMatch = systems.find((sys) => {
    try {
      return new URL(sys.url, origin).origin === origin;
    } catch {
      return false;
    }
  });

  return configMatch?.name || fallback;
}

function mapApiOptions(
  items: RepoSelectOption[],
  authService: AuthService,
): HeaderRepoSelectOption[] {
  return items.map((item) => {
    const split = splitRepoCodeUrl(item.code);
    if (!split) {
      return { label: item.text, route: item.code };
    }
    return {
      label: item.text,
      route: authService.buildCrossAppUrl(split.baseUrl, split.entryPath),
    };
  });
}

function mapConfigOptions(
  systems: SystemConfig[],
  authService: AuthService,
): HeaderRepoSelectOption[] {
  return systems.map((sys) => {
    try {
      const url = new URL(
        sys.url,
        typeof window !== 'undefined' ? window.location.origin : sys.url,
      );
      return {
        label: sys.name,
        route: authService.buildCrossAppUrl(url.origin, '/app'),
      };
    } catch {
      return { label: sys.name, route: sys.url };
    }
  });
}

/**
 * Selector de sistemas del header (GET /selects/repo-selects).
 * Si el API no trae opciones, usa `config.json` → `systems` (mismo catálogo que admin).
 * Llamar desde el contexto de inyección del layout.
 */
export function createHeaderRepoSelect(defaultLabel: string): {
  currentAppLabel: ReturnType<typeof computed<string>>;
  appOptions: ReturnType<typeof computed<HeaderRepoSelectOption[]>>;
} {
  const authService = inject(AuthService);
  const configService = inject(ConfigService);
  const repoSelectsService = inject(RepoSelectsService);
  const repoItems = toSignal(repoSelectsService.getOptions(), {
    initialValue: [] as RepoSelectOption[],
  });

  const currentAppLabel = computed(() =>
    matchCurrentRepoLabel(repoItems(), configService.systems, defaultLabel),
  );

  const appOptions = computed<HeaderRepoSelectOption[]>(() => {
    const fromApi = mapApiOptions(repoItems(), authService);
    if (fromApi.length > 0) {
      return fromApi;
    }
    return mapConfigOptions(configService.systems, authService);
  });

  return { currentAppLabel, appOptions };
}
